import React, { useState } from "react";
import {
  YStack,
  XStack,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner,
  Input,
} from "tamagui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions, Platform, Alert, ScrollView } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import { useDispatch } from "react-redux";
import { ADD_INTEGRATION, REMOVE_INTEGRATION } from "@reduxLocal/actions/types";

const PayStackOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  const [publicKeyInput, setPublicKeyInput] = useState('');
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [showKeyInputs, setShowKeyInputs] = useState(false);
  const [publicKeyError, setPublicKeyError] = useState('');
  const [secretKeyError, setSecretKeyError] = useState('');

  // Query for PayStack profile - enable if we might have keys
  // Handle 400 (keys not found) as valid state, not an error
  const { data: profileData, isLoading: profileLoading, error: profileError, refetch: refetchProfile } = useQuery({
    queryKey: ['paystackProfile'],
    queryFn: async () => {
      try {
        return await apis.getPayStackProfile();
      } catch (error: any) {
        // If 400 error means "keys not found", treat as valid state (not connected)
        // Only throw for actual errors (500, network issues, etc.)
        if (error?.response?.status === 400 && error?.response?.data?.error === 'PayStack keys not found') {
          return { 
            authorised: false, 
            profile: null,
            keysNotFound: true 
          };
        }
        // Re-throw actual errors
        throw error;
      }
    },
    enabled: true, // Try to fetch on mount to check if already connected
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error: any) => {
      // Don't retry on 400 (keys not found) - it's a valid state
      if (error?.response?.status === 400 && error?.response?.data?.error === 'PayStack keys not found') {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  // Mutation to store API keys
  const storeKeysMutation = useMutation({
    mutationFn: async (keys: { publicKey: string; secretKey: string }) => {
      return await apis.storePayStackKeys(keys.publicKey, keys.secretKey);
    },
    onSuccess: (data) => {
      setPublicKeyInput('');
      setSecretKeyInput('');
      setShowKeyInputs(false);
      setPublicKeyError('');
      setSecretKeyError('');
      // Update Redux store to reflect connection
      dispatch({ 
        type: ADD_INTEGRATION, 
        payload: { 
          authType: 'paystack', 
          data: {
            keysStored: true, // Don't store actual keys in Redux
            merchantEmail: data.profile?.merchantEmail,
            keysStoredAt: new Date().toISOString()
          }
        } 
      });
      queryClient.invalidateQueries({ queryKey: ['paystackProfile'] });
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      refetchProfile();
    },
    onError: (error: any) => {
      console.error('[PayStack Options] Key storage error:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Failed to store API keys. Please check your keys and try again.';
      
      // Set specific error messages
      if (error?.response?.status === 400) {
        if (errorMessage.toLowerCase().includes('public')) {
          setPublicKeyError(errorMessage);
        } else if (errorMessage.toLowerCase().includes('secret')) {
          setSecretKeyError(errorMessage);
        } else {
          setPublicKeyError(errorMessage);
          setSecretKeyError(errorMessage);
        }
      } else {
        setPublicKeyError(errorMessage);
        setSecretKeyError(errorMessage);
      }
      
      // Show alert with detailed error
      if (error?.response?.status === 400) {
        Alert.alert(
          'Invalid API Keys',
          errorMessage + '\n\nPlease ensure:\n• Your public key starts with pk_\n• Your secret key starts with sk_\n• Both keys are from the same PayStack account\n• The keys are valid and active',
          [{ text: 'OK' }]
        );
      }
    }
  });

  // Mutation to remove API keys
  const removeKeysMutation = useMutation({
    mutationFn: async () => {
      console.log('[PayStack Options] Removing API keys...');
      const result = await apis.removePayStackKeys();
      console.log('[PayStack Options] Remove keys response:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('[PayStack Options] Keys removed successfully:', data);
      // Remove from Redux store
      dispatch({ 
        type: REMOVE_INTEGRATION, 
        payload: 'paystack'
      });
      // Reset query data to reflect "keys not found" state
      queryClient.setQueryData(['paystackProfile'], { 
        authorised: false, 
        profile: null,
        keysNotFound: true 
      });
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['paystackProfile'] });
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      // Refetch profile to update UI state
      refetchProfile();
      // Show success message
      Alert.alert(
        'Success',
        'PayStack API keys have been removed successfully.',
        [{ text: 'OK' }]
      );
    },
    onError: (error: any) => {
      console.error('[PayStack Options] Error removing keys:', error);
      console.error('[PayStack Options] Error details:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message
      });
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to remove API keys. Please try again.';
      Alert.alert(
        'Error',
        errorMessage,
        [{ text: 'OK' }]
      );
    }
  });

  const handleStoreKeys = () => {
    // Reset errors
    setPublicKeyError('');
    setSecretKeyError('');

    // Validate inputs
    if (!publicKeyInput.trim()) {
      setPublicKeyError('Please enter your PayStack public key');
      return;
    }

    if (!publicKeyInput.trim().startsWith('pk_')) {
      setPublicKeyError('Public key must start with pk_');
      return;
    }

    if (!secretKeyInput.trim()) {
      setSecretKeyError('Please enter your PayStack secret key');
      return;
    }

    if (!secretKeyInput.trim().startsWith('sk_')) {
      setSecretKeyError('Secret key must start with sk_');
      return;
    }

    storeKeysMutation.mutate({
      publicKey: publicKeyInput.trim(),
      secretKey: secretKeyInput.trim()
    });
  };

  const handleRemoveKeys = () => {
    Alert.alert(
      'Remove PayStack Integration',
      'Are you sure you want to remove your PayStack API keys? This will disconnect your PayStack integration.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => removeKeysMutation.mutate()
        }
      ]
    );
  };

  const profile = profileData?.profile || {};

  // Show connected state if keys are stored (authorised: true) or if profile exists
  // merchantEmail can be null even when keys are stored, so check authorised flag
  const isConnected = profileData && (profileData.authorised === true || profileData.profile);

  // Debug logging to track connection state
  React.useEffect(() => {
    if (__DEV__ && !profileLoading) {
      if (isConnected) {
        console.log('[PayStack Options] Keys detected - showing connected state', {
          authorised: profileData?.authorised,
          hasProfile: !!profileData?.profile,
          merchantEmail: profile?.merchantEmail,
          keyType: profile?.keyType
        });
      } else if (profileData?.keysNotFound) {
        console.log('[PayStack Options] Keys not found - showing setup instructions');
      } else if (profileError) {
        console.log('[PayStack Options] Error fetching profile:', profileError);
      }
    }
  }, [isConnected, profileLoading, profileData, profileError]);

  if (isConnected) {
    return (
      <ErrorBoundary>
        <ScrollView showsVerticalScrollIndicator={false}>
          <YStack gap="$4" paddingBottom="$4">
            {/* Header Card */}
            <Animatable.View animation="fadeInUp" duration={600}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <XStack alignItems="center" gap="$3" marginBottom="$4">
                  <View style={{
                    backgroundColor: theme ? "#e0f2fe" : "#0c4a6e",
                    borderRadius: 12,
                    padding: 12
                  }}>
                    <MaterialCommunityIcons name="credit-card" size={32} color="#00D9FF" />
                  </View>
                  <YStack flex={1}>
                    <H3
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                    >
                      PayStack Connected
                    </H3>
                    <SizableText
                      size="$3"
                      color={theme ? "#64748b" : "#94a3b8"}
                      marginTop="$1"
                    >
                      Your PayStack account is fully integrated with SmartSaaS
                    </SizableText>
                  </YStack>
                </XStack>
              </Card>
            </Animatable.View>

            {/* Account Details */}
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <XStack alignItems="center" justifyContent="space-between" marginBottom="$4">
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Account Information
                  </H4>
                  <Button
                    backgroundColor="#00D9FF"
                    color="#000000"
                    size="$3"
                    onPress={() => {
                      Linking.openURL('https://dashboard.paystack.com');
                    }}
                    borderRadius={12}
                    paddingHorizontal="$4"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="dashboard" size={16} color="#000000" />
                      <SizableText color="#000000" fontWeight="600" size="$3">
                        Dashboard
                      </SizableText>
                    </XStack>
                  </Button>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                <YStack gap="$3">
                  <DetailRow
                    icon="email"
                    label="Merchant Email"
                    value={profile.merchantEmail || 'N/A'}
                    theme={theme}
                  />
                  {profile.businessName && (
                    <DetailRow
                      icon="business"
                      label="Business Name"
                      value={profile.businessName}
                      theme={theme}
                    />
                  )}
                  {profile.currency && (
                    <DetailRow
                      icon="attach-money"
                      label="Currency"
                      value={profile.currency.toUpperCase()}
                      theme={theme}
                    />
                  )}
                  {profile.keyType && (
                    <DetailRow
                      icon="vpn-key"
                      label="Key Type"
                      value={profile.keyType === 'live' ? 'Live Keys' : 'Test Keys'}
                      theme={theme}
                    />
                  )}
                </YStack>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    PayStack Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    Your PayStack account is fully integrated. You can now accept payments and manage transactions directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>

            {/* Remove Integration */}
            <Animatable.View animation="fadeInUp" duration={600} delay={300}>
              <Card
                backgroundColor={theme ? "#fef2f2" : "#450a0a"}
                borderColor={theme ? "#fecaca" : "#7f1d1d"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack gap="$3">
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Remove Integration
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    Remove your PayStack API keys to disconnect this integration.
                  </SizableText>
                  <Button
                    backgroundColor="#ef4444"
                    color="white"
                    onPress={handleRemoveKeys}
                    borderRadius={12}
                    paddingVertical="$3"
                    disabled={removeKeysMutation.isPending}
                  >
                    {removeKeysMutation.isPending ? (
                      <Spinner size="small" color="white" />
                    ) : (
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="delete-outline" size={18} color="white" />
                        <SizableText color="white" fontWeight="600" size="$4">
                          Remove API Keys
                        </SizableText>
                      </XStack>
                    )}
                  </Button>
                </YStack>
              </Card>
            </Animatable.View>
          </YStack>
        </ScrollView>
      </ErrorBoundary>
    );
  }

  // Show setup instructions if not connected
  return (
    <ErrorBoundary>
      <ScrollView showsVerticalScrollIndicator={false}>
        <YStack gap="$4" paddingBottom="$4">
          {/* Header Card */}
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#e0f2fe" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 12
                }}>
                  <MaterialCommunityIcons name="credit-card" size={32} color="#00D9FF" />
                </View>
                <YStack flex={1}>
                  <H3
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Connect PayStack
                  </H3>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Accept payments and manage transactions directly from SmartSaaS using your PayStack account
                  </SizableText>
                </YStack>
              </XStack>
            </Card>
          </Animatable.View>

          {/* Step 1: Get API Keys */}
          <Animatable.View animation="fadeInUp" duration={600} delay={100}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#fef3c7" : "#78350f",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons name="vpn-key" size={24} color="#f59e0b" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Step 1: Get Your API Keys
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Retrieve your API keys from your PayStack dashboard
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$3">
                <InstructionStep
                  number={1}
                  text="Log in to your PayStack Dashboard"
                  theme={theme}
                />
                <InstructionStep
                  number={2}
                  text="Navigate to Settings → API Keys & Webhooks"
                  theme={theme}
                />
                <InstructionStep
                  number={3}
                  text="Copy your Public Key (starts with pk_test_ or pk_live_)"
                  theme={theme}
                />
                <InstructionStep
                  number={4}
                  text="Copy your Secret Key (starts with sk_test_ or sk_live_)"
                  theme={theme}
                />
                <InstructionStep
                  number={5}
                  text="Keep your Secret Key secure - never share it publicly"
                  theme={theme}
                />

                <Button
                  backgroundColor="#00D9FF"
                  color="#000000"
                  onPress={() => Linking.openURL('https://dashboard.paystack.com/#/settings/developer')}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                  marginTop="$2"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={20} color="#000000" />
                    <SizableText color="#000000" fontWeight="600">
                      Open PayStack Dashboard
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Step 2: Enter API Keys */}
          <Animatable.View animation="fadeInUp" duration={600} delay={200}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons name="lock" size={24} color="#3b82f6" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    Step 2: Enter Your API Keys
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Securely enter your API keys below. They will be encrypted and stored safely.
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              {!showKeyInputs ? (
                <Button
                  backgroundColor="#00D9FF"
                  color="#000000"
                  onPress={() => setShowKeyInputs(true)}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="vpn-key" size={18} color="#000000" />
                    <SizableText color="#000000" fontWeight="600" size="$4">
                      Enter API Keys
                    </SizableText>
                  </XStack>
                </Button>
              ) : (
                <YStack gap="$3">
                  <YStack gap="$2">
                    <SizableText
                      size="$3"
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="600"
                    >
                      Public Key
                    </SizableText>
                    <Input
                      placeholder="pk_test_..."
                      value={publicKeyInput}
                      onChangeText={(text) => {
                        setPublicKeyInput(text);
                        setPublicKeyError('');
                      }}
                      secureTextEntry
                      backgroundColor={theme ? "#f8fafc" : "#334155"}
                      borderColor={publicKeyError ? "#ef4444" : (theme ? "#e2e8f0" : "#475569")}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontSize="$4"
                      padding="$3"
                      borderRadius={8}
                    />
                    {publicKeyError && (
                      <SizableText size="$3" color="#ef4444">
                        {publicKeyError}
                      </SizableText>
                    )}
                    <SizableText
                      size="$2"
                      color={theme ? "#64748b" : "#94a3b8"}
                    >
                      Your public key starts with pk_test_ (test) or pk_live_ (live)
                    </SizableText>
                  </YStack>

                  <YStack gap="$2">
                    <SizableText
                      size="$3"
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="600"
                    >
                      Secret Key
                    </SizableText>
                    <Input
                      placeholder="sk_test_..."
                      value={secretKeyInput}
                      onChangeText={(text) => {
                        setSecretKeyInput(text);
                        setSecretKeyError('');
                      }}
                      secureTextEntry
                      backgroundColor={theme ? "#f8fafc" : "#334155"}
                      borderColor={secretKeyError ? "#ef4444" : (theme ? "#e2e8f0" : "#475569")}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontSize="$4"
                      padding="$3"
                      borderRadius={8}
                    />
                    {secretKeyError && (
                      <SizableText size="$3" color="#ef4444">
                        {secretKeyError}
                      </SizableText>
                    )}
                    <SizableText
                      size="$2"
                      color={theme ? "#64748b" : "#94a3b8"}
                    >
                      Your secret key starts with sk_test_ (test) or sk_live_ (live). Keep this key secure and never share it.
                    </SizableText>
                  </YStack>

                  <XStack gap="$3">
                    <Button
                      flex={1}
                      backgroundColor={theme ? "#f1f5f9" : "#334155"}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      onPress={() => {
                        setShowKeyInputs(false);
                        setPublicKeyInput('');
                        setSecretKeyInput('');
                        setPublicKeyError('');
                        setSecretKeyError('');
                      }}
                      borderRadius={12}
                      paddingVertical="$3"
                    >
                      <SizableText fontWeight="600" size="$4">Cancel</SizableText>
                    </Button>
                    <Button
                      flex={1}
                      backgroundColor="#00D9FF"
                      color="#000000"
                      onPress={handleStoreKeys}
                      borderRadius={12}
                      paddingVertical="$3"
                      disabled={storeKeysMutation.isPending}
                    >
                      {storeKeysMutation.isPending ? (
                        <Spinner size="small" color="#000000" />
                      ) : (
                        <SizableText color="#000000" fontWeight="600" size="$4">
                          Connect
                        </SizableText>
                      )}
                    </Button>
                  </XStack>

                  <SizableText
                    size="$2"
                    color={theme ? "#64748b" : "#94a3b8"}
                    textAlign="center"
                  >
                    Your keys are encrypted and stored securely. They are never displayed in plain text.
                  </SizableText>
                </YStack>
              )}
            </Card>
          </Animatable.View>

          {/* Key Information */}
          <Animatable.View animation="fadeInUp" duration={600} delay={300}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <XStack alignItems="center" gap="$3" marginBottom="$4">
                <View style={{
                  backgroundColor: theme ? "#f3e8ff" : "#581c87",
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons name="info" size={24} color="#8b5cf6" />
                </View>
                <YStack flex={1}>
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                  >
                    About API Keys
                  </H4>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    Understanding test vs live keys
                  </SizableText>
                </YStack>
              </XStack>

              <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

              <YStack gap="$3">
                <YStack gap="$2">
                  <SizableText
                    size="$3"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Test Keys (pk_test_ / sk_test_)
                  </SizableText>
                  <SizableText
                    size="$4"
                    color={theme ? "#64748b" : "#94a3b8"}
                    lineHeight={20}
                  >
                    Use test keys for development and testing. Transactions made with test keys are not real and won't charge actual money.
                  </SizableText>
                </YStack>

                <YStack gap="$2">
                  <SizableText
                    size="$3"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Live Keys (pk_live_ / sk_live_)
                  </SizableText>
                  <SizableText
                    size="$4"
                    color={theme ? "#64748b" : "#94a3b8"}
                    lineHeight={20}
                  >
                    Use live keys for production. Transactions made with live keys are real and will charge actual money. Only use live keys when you're ready to accept real payments.
                  </SizableText>
                </YStack>

                <YStack gap="$2" marginTop="$2">
                  <SizableText
                    size="$3"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Security Best Practices
                  </SizableText>
                  <SizableText
                    size="$4"
                    color={theme ? "#64748b" : "#94a3b8"}
                    lineHeight={20}
                  >
                    • Never share your secret key publicly{'\n'}
                    • Use test keys during development{'\n'}
                    • Rotate keys if you suspect they've been compromised{'\n'}
                    • Keep your secret key secure and never commit it to version control
                  </SizableText>
                </YStack>
              </YStack>
            </Card>
          </Animatable.View>

          {/* Error State - Only show for actual errors, not "keys not found" */}
          {profileError && !profileLoading && !profileData?.keysNotFound && (
            <Animatable.View animation="fadeInUp" duration={600}>
              <Card
                backgroundColor={theme ? "#fef2f2" : "#450a0a"}
                borderColor={theme ? "#fecaca" : "#7f1d1d"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$4">
                  <View style={{
                    backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                    borderRadius: 50,
                    padding: 16
                  }}>
                    <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                  </View>
                  <H4 color="#ef4444" fontWeight="600" textAlign="center">
                    Connection Error
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#64748b" : "#94a3b8"}
                    textAlign="center"
                  >
                    {profileError?.message || 'Failed to connect to PayStack. Please check your API keys and try again.'}
                  </SizableText>
                  <Button
                    backgroundColor="#ef4444"
                    color="white"
                    onPress={() => refetchProfile()}
                    borderRadius={12}
                    paddingHorizontal="$5"
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="refresh" size={20} color="white" />
                      <SizableText color="white" fontWeight="600">
                        Retry Connection
                      </SizableText>
                    </XStack>
                  </Button>
                </YStack>
              </Card>
            </Animatable.View>
          )}

          {/* Loading State */}
          {profileLoading && (
            <Animatable.View animation="fadeIn" duration={600}>
              <Card
                backgroundColor={theme ? "#f8fafc" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$6"
                elevate
              >
                <YStack alignItems="center" gap="$4">
                  <Spinner size="large" color="#00D9FF" />
                  <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                    Loading PayStack account...
                  </H4>
                </YStack>
              </Card>
            </Animatable.View>
          )}
        </YStack>
      </ScrollView>
    </ErrorBoundary>
  );
};

const InstructionStep = ({ number, text, theme }: { number: number; text: string; theme: boolean }) => (
  <XStack alignItems="flex-start" gap="$3">
    <View style={{
      backgroundColor: theme ? "#e0f2fe" : "#0c4a6e",
      borderRadius: 20,
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <SizableText
        size="$3"
        color="#00D9FF"
        fontWeight="700"
      >
        {number}
      </SizableText>
    </View>
    <SizableText
      size="$4"
      color={theme ? "#1e293b" : "#f1f5f9"}
      flex={1}
      lineHeight={24}
    >
      {text}
    </SizableText>
  </XStack>
);

const DetailRow = ({ icon, label, value, theme }: { icon: string; label: string; value: string; theme: boolean }) => (
  <XStack alignItems="center" gap="$3">
    <View style={{
      backgroundColor: theme ? "#e0f2fe" : "#0c4a6e",
      borderRadius: 8,
      padding: 8
    }}>
      <MaterialIcons name={icon as any} size={20} color="#00D9FF" />
    </View>
    <YStack flex={1}>
      <SizableText
        size="$2"
        color={theme ? "#64748b" : "#94a3b8"}
      >
        {label}
      </SizableText>
      <SizableText
        size="$4"
        color={theme ? "#1e293b" : "#f1f5f9"}
        fontWeight="600"
      >
        {value}
      </SizableText>
    </YStack>
  </XStack>
);

export default PayStackOptions;
